import { Button, Center, Divider, Flex, Heading, Text } from "@chakra-ui/react";
import hj from "@hotjar/browser";
import { type Curves } from "@taquito/signer";
import { type ChainId } from "@umami/chains";
import { useDynamicModalContext } from "@umami/components";
import { useHandleSession } from "@umami/state";
import { type PropsWithChildren } from "react";

import { OnboardWithFacebookButton } from "./OnboardWithFacebookButton";
import { OnboardWithGoogleButton } from "./OnboardWithGoogleButton";
import { OnboardWithRedditButton } from "./OnboardWithRedditButton";
import { OnboardWithTwitterButton } from "./OnboardWithTwitterButton";
import { usePasskey } from "../../../hooks/usePasskey";
import { useColor } from "../../../styles/useColor";
import { trackButtonClick } from "../../../utils/analytics";
import { AccountTileWrapper } from "../../AccountTile";
import { NameAccountModal } from "../../NameAccountModal";
import { ImportWallet } from "../ImportWallet";
import { SetupPassword } from "../SetupPassword";

export const OnboardOptions = ({ children }: PropsWithChildren) => {
  const color = useColor();
  const { openWith, onClose } = useDynamicModalContext();
  const { isSessionActive } = useHandleSession();
  const { createMnemonicWallet, isPasskeyFlow, isLoading, isPasskeyUser } = usePasskey();
  hj.stateChange("onboardOptions");

  const createNewWalletPasskey = async () => {
    if (isSessionActive) {
      const handleNameAccount = async ({
        accountName: label,
        derivationPaths,
        curve,
      }: {
        accountName: string;
        derivationPaths?: Record<ChainId, string>;
        curve?: Curves;
      }) => {
        await createMnemonicWallet({ label, derivationPaths, curve });
        onClose();
      };

      return openWith(
        <NameAccountModal
          buttonLabel="Continue"
          onSubmit={handleNameAccount}
          subtitle={"Name your account or edit your\n account name later."}
          title="Create Account"
        />
      );
    } else {
      await createMnemonicWallet();
    }
  };

  const handleCreateNewWallet = async () => {
    trackButtonClick("create_new_wallet", {
      button_label: "Create a new wallet",
      section: "Welcome",
      flow: "Onboarding",
      action: "button_click",
    });

    if (isPasskeyFlow && isPasskeyUser) {
      await createNewWalletPasskey();
    } else {
      await createNewWalletMnemonic();
    }
  };

  const createNewWalletMnemonic = async () => {
    if (isSessionActive) {
      const handleNameAccount = () => openWith(<SetupPassword mode="add_account" />);

      return openWith(
        <NameAccountModal
          buttonLabel="Continue"
          onSubmit={handleNameAccount}
          subtitle={"Name your account or edit your\n account name later."}
          title="Create Account"
        />
      );
    } else {
      return openWith(<SetupPassword mode="new_mnemonic" />);
    }
  };

  return (
    <Flex alignItems="center" flexDirection="column" width="full">
      <Heading color={color("900")} size="lg">
        Continue with:
      </Heading>

      <Center flexDirection="column" gap="36px" width="full" height="full">
        <Flex gap={{ base: "24px", md: "30px" }} marginTop="30px" color={color("white", "black")}>
          <AccountTileWrapper size="md">
            <OnboardWithGoogleButton />
          </AccountTileWrapper>

          <AccountTileWrapper size="md">
            <OnboardWithFacebookButton />
          </AccountTileWrapper>

          <AccountTileWrapper size="md">
            <OnboardWithTwitterButton />
          </AccountTileWrapper>

          <AccountTileWrapper size="md">
            <OnboardWithRedditButton />
          </AccountTileWrapper>
        </Flex>

        <Center gap="4px" width="full">
          <Divider width="full" />
          <Text color={color("500")} size="lg">
            or
          </Text>
          <Divider width="full" />
        </Center>

        <Flex flexDirection="column" gap="12px" width="full">
          <Button
            width="full"
            isLoading={isLoading}
            onClick={handleCreateNewWallet}
            size="lg"
            variant="primary"
          >
            Create a new wallet
          </Button>
          <Button
            width="full"
            onClick={() => {
              trackButtonClick("already_have_wallet", {
                button_label: "I already have a wallet",
                section: "Welcome",
                flow: "Onboarding",
                action: "button_click",
              });

              return openWith(<ImportWallet />, {
                size: "xl",
              });
            }}
            size="lg"
            variant="secondary"
          >
            I already have a wallet
          </Button>
        </Flex>

        {children}
      </Center>
    </Flex>
  );
};
